import { SceneType } from "@/frameworks/libraries/commons";
import { Properties } from "@/frameworks/libraries/properties";
import { Configure } from "@/frameworks/libraries/utilities";

import { SpaceService } from "../API/user";
import { usePersistActions } from "../persistence";
import MeetingService from "../API/meeting/private/api";
import MyRoomServiceAPI from "../API/user/useMyRoom/MyRoomServiceAPI";

type VerifyUserInfoUpdatedType = Partial<WebServices.User.UserInfo> | null;

const LOG_TAG = "[UnitySceneManager]";
export default class UnitySceneManager {
	private static _instance: UnitySceneManager | null = null;
	private constructor() {}
	public static get instance() {
		if (!UnitySceneManager._instance) {
			UnitySceneManager._instance = new UnitySceneManager();
		}
		return UnitySceneManager._instance;
	}
	private meetingAPI = MeetingService;
	private SpaceAPI = new SpaceService({});
	private MyRoomAPI = MyRoomServiceAPI.getInstance({});

	/**
	 * 유니티에서 Scene 입장 시, 처리를 진행합니다.
	 * reason 1. 유니티 내에서 서버 에러가 발생할 경우 reconnect 처리로 인한 enterProcess
	 * reason 2. FE 쪽 네트워크 단절로 인한 space reconnect시의 enter / leave verifications
	 * 사용자 currentSpace 정보는 stomp로 실시간으로 전달되지 않기 떄문에, enter / leave process
	 * 정상 처리 후, restAPI 로 currentSpace를 정의합니다.
	 */
	public unity_enter_process = async (
		caller: string,
		args: IPC.Spaces.EnterSpace
	): Promise<VerifyUserInfoUpdatedType> => {
		console.debug(LOG_TAG, caller, "unity_enter_API_process", args);
		const spaceCategoryType = checkSpaceCategoryType(args);

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let resolver: Promise<any> = Promise.resolve();
		try {
			switch (spaceCategoryType) {
				case "CONFERENCE": {
					resolver = this.meetingAPI.conference
						.participate({
							conferenceId: args.spaceID,
							accessCode: Configure.currentConference?.accessCode,
							deviceKey: Properties.browserTabID
						})
						.then(() => {
							usePersistActions().unity.set_my_current_space({
								currentConference: {
									conferenceId: args.spaceID,
									participatedTime: new Date().getTime()
									// sceneType: res.data.spaceScene?.sceneType.type,
									// sceneLevel: res.data.spaceScene?.sceneLevel
								}
							});
						});
					break;
				}
				case "MY_ROOM":
				case "MY_ROOM_EDIT": {
					resolver = this.MyRoomAPI.myRoom.participate
						.execute({
							deviceKey: Properties.browserTabID,
							myRoomId: args.spaceID
						})
						.then((res) => {
							usePersistActions().unity.set_my_current_space({
								currentSpace: {
									spaceId: args.spaceID,
									spaceType: res.data.spaceType,
									spaceScene: res.data.spaceScene
								}
							});
						});
					break;
				}
				case "OPEN_WORLD": {
					resolver = this.SpaceAPI.spacePublicOpenWorld.participate
						.execute({
							deviceKey: Properties.browserTabID,
							openWorldChannelId: String(args.gameChannelId || ""),
							spaceId: args.spaceID
						})
						.then((res) => {
							usePersistActions().unity.set_my_current_space({
								currentSpace: {
									spaceId: args.spaceID,
									spaceType: "SPACE",
									spaceScene: res.data!.spaceScene
								}
							});
						});

					break;
				}
				case "GROUP_SPACE": {
					resolver = this.SpaceAPI.space.enter
						.execute({
							spaceId: args.spaceID,
							deviceKey: Properties.browserTabID
						})
						.then((res) => {
							usePersistActions().unity.set_my_current_space({
								currentSpace: {
									spaceId: args.spaceID,
									spaceType: "SPACE",
									spaceScene: res.data!.spaceScene
								}
							});
						});
					break;
				}
			}
		} catch (error) {
			console.error("Error during unity_enter_process:", error);
			throw error;
		}
		return resolver.then(() => {
			return usePersistActions().users.refresh_user(Configure.userID, true);
		});
	};
	public unity_leave_process = async (
		caller: string,
		args: IPC.Spaces.ExitSpace,
		normalLeave: boolean = false
	): Promise<VerifyUserInfoUpdatedType> => {
		console.debug(LOG_TAG, caller, "unity_leave_API_process", args);
		const spaceCategoryType = checkSpaceCategoryType(args);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let resolver: Promise<any> = Promise.resolve();
		try {
			switch (spaceCategoryType) {
				case "CONFERENCE": {
					const detailMeeting = await this.meetingAPI.conference.detail({ conferenceId: args.spaceID || "" });
					if (detailMeeting && detailMeeting.data) {
						console.log(detailMeeting.data);
						const user = (detailMeeting.data as SwaggerMeetingAPIModels.Conference).participants.find(
							(participant) => participant?.user?.id === Configure.userID
						);
						if (user) {
							resolver = this.meetingAPI.conference
								.leave({
									conferenceId: args.spaceID,
									participantId: user.id,
									normalLeave
								})
								.then(() => {
									usePersistActions().unity.set_my_current_space({
										currentConference: undefined
									});
								});
						}
					}
					break;
				}
				case "MY_ROOM":
				case "MY_ROOM_EDIT": {
					resolver = this.MyRoomAPI.myRoom.leave
						.execute({
							myRoomId: args.spaceID,
							deviceKey: Properties.browserTabID
						})
						.then(() => {
							usePersistActions().unity.set_my_current_space({
								currentSpace: undefined
							});
						});
					break;
				}
				case "OPEN_WORLD":
				case "GROUP_SPACE": {
					resolver = this.SpaceAPI.space.leave
						.execute({
							spaceId: args.spaceID,
							deviceKey: Properties.browserTabID
						})
						.then(() => {
							usePersistActions().unity.set_my_current_space({
								currentSpace: undefined
							});
						});

					break;
				}
				default: {
					resolver = Promise.reject("Invalid space category type");
				}
			}
		} catch (error) {
			console.error("Error during unity_leave_process:", error);
			throw error;
		}
		return resolver.then(() => {
			/*
			* @description
			use refresh_user with forceUpdate flag to update currentSpace
			*/
			return usePersistActions().users.refresh_user(Configure.userID, true);
		});
	};
}

/**
 * @description check space category type cuz BE API is distributed each space type
 */
function checkSpaceCategoryType(args: IPC.Spaces.EnterSpace | IPC.Spaces.ExitSpace): FE_SPACE_TYPE {
	if (args.meetingType === "CONFERENCE") {
		return "CONFERENCE";
	}
	if (args.sceneType === SceneType.MyRoomScene) {
		return "MY_ROOM";
	}
	if (args.sceneType === SceneType.MyRoomEditScene) {
		return "MY_ROOM_EDIT";
	}
	if (args.sceneType === SceneType.OpenWorldScene) {
		return "OPEN_WORLD";
	}
	return "GROUP_SPACE";
}
